import Settings from '@/components/buy-stack/ProductSelectSettings';
import {
  DeliveryOption,
  DeliveryOptionRecurring,
} from '@/product/delivery-option';
import {getColorStyle} from '@/utilities/color-style-map';
import plural from '@/utilities/plural';
import classNames from 'classnames';
import {useEffect, useState} from 'preact/hooks';

/**
 * A selector for choosing a selling plan. It displays the frequency of the plan and the interval unit.
 * It also displays the subscription terms if provided.
 * The `options` prop is an array of selling plans to choose from.
 * The `settings` prop is an object containing settings for the selector.
 * When an option is selected, the `onUpdate` callback will be invoked with the selected option.
 *See [DeliveryOption] and [DeliveryOptionRecurring] for more information about selling plans.
 */
export default function SellingPlanSelector({
  color = 'primary-blue',
  onUpdate,
  options,
  value,
}: SellingPlanSelectorProps) {
  const [selected, setSelected] = useState<DeliveryOption | null>(
    value ?? options[0] ?? null
  );

  useEffect(() => {
    setSelected(value ?? options[0] ?? null);
  }, [value, options]);

  const frequency = selected?.sellingPlan!.orderIntervalFrequency ?? 0;

  const intervalUnitLabel = selected?.sellingPlan?.intervalUnit ?? 'month';

  const handleSelectOption = (option: DeliveryOption) => {
    setSelected(option);
    onUpdate && onUpdate(option);
  };

  const colorStyle = getColorStyle(color);

  return (
    <Settings.Consumer>
      {({delivery_interval_label}) => (
        <div className="grid grid-cols-4 gap-3 text-neutral-dark-gray">
          {options.map((option) => {
            const isSelected = option === selected;
            return (
              <button
                data-testid={'order-interval-' + option.sellingPlan.orderIntervalFrequency}
                key={option.sellingPlan.id}
                className={classNames(
                  'flex h-14 items-center justify-center rounded-md border-2 fast-transition',
                  {
                    [`${colorStyle.border} ${colorStyle.background} text-bright-white`]:
                      isSelected,
                    'border-neutral-gray bg-bright-white': !isSelected,
                  }
                )}
                onClick={() => handleSelectOption(option)}
              >
                <h3 className="md:text-3xl">
                  {option.sellingPlan.orderIntervalFrequency}
                </h3>
              </button>
            );
          })}

          {selected && (
            <label className="col-span-full flex w-max items-center justify-center gap-x-1 justify-self-center font-semibold uppercase text-neutral-dark-gray xs:text-lg">
              {delivery_interval_label}
              <span
                className={classNames(
                  'rounded  px-2 text-bright-white',
                  colorStyle.background
                )}
              >
                {selected.sellingPlan?.orderIntervalFrequency}
              </span>
              {plural(frequency, intervalUnitLabel)}
            </label>
          )}
        </div>
      )}
    </Settings.Consumer>
  );
}

export type SellingPlanSelectorProps = {
  color?: string;
  onUpdate?: (option: DeliveryOption) => void;
  options: DeliveryOptionRecurring[];
  value?: DeliveryOptionRecurring;
};
